import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import FullIcon from '@panel/commons/assets/icons/full-icon.svg';
import PositiveIcon from '@panel/commons/assets/icons/positivo_hover.svg';
import WarmIcon from '@panel/commons/assets/icons/neutro_hover.svg';
import NegativeIcon from '@panel/commons/assets/icons/negativo_hover.svg';
import NeutralIcon from '../../assets/icons/noQualityFace.svg';

const siteId = process.env.REACT_APP_BUILD_SITE;
const isBrazilPortal = siteId && siteId.includes('BR');

const Container = styled.div`
  width: 100%;
  height: fit-content;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  @media screen and (max-width: 375px) {
    justify-content: space-between;
  }
`;

const Icon = styled.div`
  width: 40px;
  height: 40px;
  background-image: ${({ icon }) => `url(${icon})`};
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 16px;
  @media screen and (max-width: 360px) {
    margin-right: 4px;
  }
`;

const TitleText = styled.div`
  font-family: 'Hind';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 130%;
  height: 21px;
  margin-right: 9px;
  @media screen and (max-width: 375px) {
    margin-right: 4px;
  }
  @media screen and (max-width: 360px) {
    margin-right: 1px;
  }
`;

const TooltipComponent = styled.div`
  padding: 3px 8px;
  width: ${({ width }) => width || '278px'};
  height: ${({ height }) => height || '64px'};
  white-space: pre-wrap;
  text-align: center;
`;

const IconTooltip = styled.img`
  margin-top: 6px;
`;

const MainArea = styled.div`
  display: flex;
  flex-direction: ${({ device }) => (device !== 'mobile' ? 'row' : 'column')};
  justify-content: ${({ device }) => (device !== 'mobile' ? 'space-between' : 'center')};
  align-items: center;
`;

const LeftSide = styled.div`
  width: ${({ device }) => (device !== 'mobile' ? '25%' : '100%')};
  display: flex;
  gap: 10px;
  flex-direction: column;
  justify-content: ${({ device }) => (device !== 'mobile' ? 'flex-start' : 'center')};
  align-items: ${({ device }) => (device !== 'mobile' ? 'flex-start' : 'center')};
  ${({ device }) =>
    device === 'tablet' &&
    css`
      width: 33%;
    `};
`;

const ProgressStyles = {
  high: {
    icon: PositiveIcon,
    barColor: select('accessibleGreen')
  },
  medium: {
    icon: WarmIcon,
    barColor: select('yellow')
  },
  low: {
    icon: NegativeIcon,
    barColor: select('negative')
  },
  noData: {
    icon: NeutralIcon,
    barColor: select('midDarkGray')
  },
  noQuality: {
    icon: NeutralIcon,
    barColor: select('midLightGray')
  }
};

const ContainerQuality = styled.div`
  width: ${({ device }) => (device === 'desktop' ? 'auto' : '100%')};
  display: flex;
  justify-content: center;
  align-items: center;
  .textContainer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-family: 'Hind';
    font-style: normal;
    .text {
      font-weight: 600;
      font-size: 16px;
      line-height: 130%;
    }
    .qualityText {
      width: 100%;
      font-weight: 700;
      font-size: ${({ qualityStatus }) => (qualityStatus === 'noQuality' ? '24' : '32')}px;
      line-height: 51px;
      text-align: ${({ device }) => (device === 'mobile' ? 'center' : 'start')};
      color: ${({ qualityStatus }) => ProgressStyles[qualityStatus].barColor};
      ${({ device }) =>
        device === 'tablet' &&
        css`
          font-size: 18px;
        `};
    }
  }
`;

const CircleChart = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 91px;
  height: 91px;
  position: relative;
  margin: 0 19px 12px 0;
  .icon-container {
    background-color: white;
    right: 2px;
    bottom: 4px;
    position: absolute;
    width: fit-content;
    height: fit-content;
    border-radius: 50%;
    padding: 1px;
    .face-icon {
      background-image: ${({ qualityStatus, isFull }) => {
        if (isFull) return `url(${FullIcon})`;
        return qualityStatus && `url(${ProgressStyles[qualityStatus].icon})`;
      }};
      min-width: ${({ isFull }) => (isFull ? 33 : 23)}px;
      min-height: ${({ isFull }) => (isFull ? 26 : 23)}px;
      background-size: cover;
    }
  }
  .single-chart {
    width: 100%;
    justify-content: space-around;
  }
  .circular-chart {
    display: block;
    max-height: 250px;
  }
  .circle-bg {
    fill: none;
    stroke: ${({ qualityStatus }) =>
      qualityStatus === 'noData' || qualityStatus === 'noQuality'
        ? ProgressStyles[qualityStatus].barColor
        : select('midLightGray')};
    stroke-width: 2.8;
  }
  .circle {
    fill: none;
    stroke-width: 2.8;
    stroke-linecap: round;
  }
  .circular-chart.blue .circle {
    stroke: ${({ qualityStatus }) => ProgressStyles[qualityStatus].barColor || select('midLightGray')};
    display: flex;
  }
  .percentage {
    fill: ${({ qualityStatus }) => (qualityStatus ? ProgressStyles[qualityStatus].barColor : select('darkGray'))};
    font-family: 'Hind';
    font-style: normal;
    font-weight: 800;
    font-size: 9px;
    text-anchor: middle;
    display: flex;
  }
  .label {
    fill: ${select('darkGray')};
    font-size: 5px;
    text-anchor: middle;
    font-weight: 400;
    display: flex;
  }
  .label_noQuality {
    fill: ${select('darkGray')};
    font-size: ${({ qualityNotApply }) => (isBrazilPortal && qualityNotApply ? '0.36em' : '0.45em')};
    text-anchor: middle;
    font-weight: 400;
    display: flex;
  }
`;

const Separator = styled.div`
  width: ${({ device }) => (device !== 'mobile' ? '1px' : '100%')};
  height: ${({ device }) => (device !== 'mobile' ? '128' : '1')}px;
  background-color: ${select('middleGray')};
  margin: ${({ device }) => (device !== 'mobile' ? '0px 50px 0px 40px' : '24px 0px')};
  ${({ device }) =>
    device === 'tablet' &&
    css`
      margin: 0px 35px;
    `};
  @media screen and (max-width: 1330px) {
    margin: ${({ device }) => device === 'desktop' && '0px 20px 10px 40px'};
  }
  @media screen and (max-width: 768px) {
    margin: ${({ device }) => device === 'tablet' && '0px 10px 0px 30px'};
  }
`;

const RightSide = styled.div`
  width: ${({ device }) => (device !== 'mobile' ? '74' : '100')}%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ device }) => (device !== 'mobile' ? '14' : '16')}px;
  ${({ device }) =>
    device === 'tablet' &&
    css`
      width: 59%;
    `};
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: ${({ device }) => (device !== 'mobile' ? 'row' : 'column')};
  justify-content: space-between;
  align-items: center;
  gap: ${({ device }) => (device !== 'mobile' ? '48' : '1')}px;
  ${({ device }) =>
    device === 'tablet' &&
    css`
      gap: 26px;
    `};
  @media screen and (max-width: 1330px) {
    gap: ${({ device }) => device === 'desktop' && '20px'};
  }
`;

const QualityText = styled.div`
  min-width: ${({ device }) => (device !== 'mobile' ? '130px' : '100%')};
  display: flex;
  align-items: center;
  .progressBar-title {
    font-family: 'Hind';
    font-style: normal;
    font-weight: ${({ hoverActive }) => (hoverActive ? '600' : '400')};
    font-size: 14px;
    line-height: 22px;
  }
  .image-faces {
    margin: 0 0 1px 8px;
  }
  ${({ device }) =>
    device === 'tablet' &&
    css`
      min-width: 125px;
    `};
`;

const ProgressBarRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ device }) => (device !== 'mobile' ? '13' : '16')}px;
  .containerValue {
    display: flex;
    gap: 3px;
  }
  .value {
    min-width: 20px;
    font-family: 'Hind';
    font-style: normal;
    font-weight: ${({ hoverActive }) => (hoverActive ? '700' : '600')};
    font-size: 14px;
    line-height: 130%;
  }
  ${({ device }) =>
    device === 'tablet' &&
    css`
      width: 270px;
    `};
`;

export {
  Container,
  HeaderArea,
  Icon,
  TitleText,
  TooltipComponent,
  IconTooltip,
  MainArea,
  LeftSide,
  ContainerQuality,
  CircleChart,
  Separator,
  RightSide,
  ProgressBarContainer,
  QualityText,
  ProgressBarRow
};
